import { PROJECT_DATA } from './modules/admin/administration/projects/project-data';
import { withFeatureAvailability } from '@core/availability/feature-availability.routes';
import { LocalProjectDataSource } from './modules/admin/administration/projects/local-project-data';
import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { AppPermissions } from '@core/permission/permission.constants';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = withFeatureAvailability([
    // Redirect empty path to '/dashboard'
    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },

    // Redirect signed-in user to the '/dashboard'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes'),
            },
            {
                path: 'forgot-password',
                loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes'),
            },
            {
                path: 'reset-password',
                loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes'),
            },
            {
                path: 'set-password',
                loadChildren: () => import('app/modules/auth/set-password/set-password.routes'),

            },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') },
            {
                path: 'confirm-email',
                loadChildren: () => import('app/modules/auth/confirm-email/confirm-email.routes'),
            },
            // {path: 'change-password', loadChildren: () => import('app/modules/auth/change-password/change-password.routes')},
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
            {
                path: 'unlock-session',
                loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes'),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [{ path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') }],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            { path: 'my-profile', loadComponent: () => import('app/modules/admin/pages/my-profile/my-profile.component').then(m => m.MyProfileComponent) },
            { path: 'help', loadComponent: () => import('app/modules/admin/pages/help/help.component').then(m => m.HelpComponent) },
            { path: 'attendance-calendar', data: { permission: AppPermissions.Report.DailyOvertimeRead }, loadComponent: () => import('app/modules/admin/pages/attendance-calendar/attendance-calendar.component').then(m => m.AttendanceCalendarComponent) },
            { path: 'attendance-overview', data: { permission: AppPermissions.Report.DailyOvertimeRead, personal: false }, loadComponent: () => import('app/modules/admin/pages/attendance-calendar/attendance-calendar.component').then(m => m.AttendanceCalendarComponent) },
            { path: 'absence-calendar', data: { permission: AppPermissions.Absence.Read, personal: true }, loadComponent: () => import('app/modules/admin/pages/absence-calendar/absence-calendar.component').then(m => m.AbsenceCalendarComponent) },
            { path: 'absence-overview', data: { permission: AppPermissions.Absence.Read, personal: false }, loadComponent: () => import('app/modules/admin/pages/absence-calendar/absence-calendar.component').then(m => m.AbsenceCalendarComponent) },
            { path:'project-report', data:{permission:AppPermissions.Employee.List}, loadComponent:()=>import('./modules/admin/pages/project-report/project-report.component').then(m=>m.ProjectReportComponent) },
            { path:'team-capacity', data:{permission:AppPermissions.Employee.List}, loadComponent:()=>import('./modules/admin/pages/team-capacity/team-capacity.component').then(m=>m.TeamCapacityComponent) },
            { path: 'team-dashboard', data: { permission: AppPermissions.Employee.List }, loadComponent: () => import('app/modules/admin/dashboard/team-dashboard.component').then(m => m.TeamDashboardComponent) },
            { path: 'dashboard', loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes') },
            {
                path: 'administration',
                children: [
                    {path:'access-control',data:{permission:AppPermissions.Employee.AssignSuperior},loadComponent:()=>import('./modules/admin/administration/rules/access-control-hub.component').then(m=>m.AccessControlHubComponent)},
{path:'projects',providers:[{provide:PROJECT_DATA,useClass:LocalProjectDataSource}],loadComponent:()=>import('./modules/admin/administration/projects/projects.component').then(m=>m.ProjectsComponent)},
{path:'company-days',loadComponent:()=>import('./modules/admin/administration/rules/company-days.component').then(m=>m.CompanyDaysComponent)},
{path:'time-limits',data:{permission:AppPermissions.TimeProfile.Manage},loadComponent:()=>import('./modules/admin/administration/rules/time-limits-overview.component').then(m=>m.TimeLimitsOverviewComponent)},
{path:'work-time-evaluation',pathMatch:'full',redirectTo:'time-limits'},
{ path: 'overview', loadComponent: () => import('./modules/admin/pages/appearance/administration-overview.component').then(m => m.AdministrationOverviewComponent) },
                    { path: 'customization', data: { permission: 'Tenant.Update' }, loadComponent: () => import('./modules/admin/pages/appearance/customization.component').then(m => m.CustomizationComponent) },
                    { path: 'billing', loadComponent: () => import('./modules/admin/pages/appearance/billing.component').then(m => m.BillingComponent) },
                    { path: 'license', loadComponent: () => import('./modules/admin/pages/appearance/license.component').then(m => m.LicenseComponent) },
                    {
                        path: 'install-app',
                        loadComponent: () => import('app/modules/admin/pages/install-app/install-app.component').then(m => m.InstallAppComponent),
                    },
                    {
                        path: 'employee-management',
                        data: { permission: AppPermissions.Employee.List },
                        loadChildren: () =>
                            import('app/modules/admin/administration/employee-management/employee-management.routes'),
                    },

                    {
                        path: 'department-management',
                        data: { permission: AppPermissions.Department.Manage },

                        loadChildren: () =>
                            import(
                                'app/modules/admin/administration/department_management/department-management.routes'
                            ),
                    },
                    {
                        path: 'location-management',
                        data: { permission: AppPermissions.Location.Manage },

                        loadChildren: () =>
                            import('app/modules/admin/administration/location_management/location-management.routes'),
                    },
                    {
                        path: 'superior-assignment',
                        data: { permission: AppPermissions.Employee.AssignSuperior },

                        loadChildren: () =>
                            import('app/modules/admin/administration/superior-assignment/superior-assignment.routes'),
                    },
                    {
                        path: 'time-profile-management',
                        data: { permission: AppPermissions.TimeProfile.Manage },

                        loadChildren: () =>
                            import(
                                'app/modules/admin/administration/time-profile-management/time-profile-management.routes'
                            ),
                    },
                    {
                        path: 'break-policy-management',
                        data: { permission: AppPermissions.BreakPolicy.Manage },

                        loadChildren: () =>
                            import(
                                'app/modules/admin/administration/break-policy-management/break-policy-management.routes'
                            ),
                    },
                    {
                        path: 'vacation-entitlement-management',
                        data: { permission: AppPermissions.Vacation.Entitlement.Update },

                        loadChildren: () =>
                            import(
                                'app/modules/admin/administration/vacation-entitlement-management/vacation-entitlement-management.routes'
                            ),
                    },
                    {
                        path: 'absence-requests',
                        data: { permission: AppPermissions.Absence.Read },

                        loadChildren: () =>
                            import('app/modules/admin/administration/absence-requests/absence-requests.routes'),
                    },
                    {
                        path: 'absence-list',
                        data: { permission: AppPermissions.Absence.Read },
                        loadChildren: () =>
                            import('app/modules/admin/administration/absence-list/absence-list.routes'),
                    },
                    {
                        path: 'absence-type-management',
                        data: { permission: AppPermissions.AbsenceType.Manage },
                        loadChildren: () =>
                            import(
                                'app/modules/admin/administration/absence-type-management/absence-type-management.routes'
                            ),
                    },
                ],
            },
            {
                path: 'work-time',
                children: [
                    {
                        path:'attendance',data:{permission:AppPermissions.Attendance.Read,attendance:true},loadComponent:()=>import('./modules/admin/administration/employee-management/list/employee-management-list.component').then(m=>m.EmployeeManagementListComponent),
                    },
                    {
                        path: 'change-requests',
                        data: { permission: AppPermissions.WorkTime.Read },
                        loadChildren: () => import('app/modules/admin/pages/work-time-requests/work-time-requests.routes'),
                    },
                    {
                        path: 'sessions',
                        data: { permission: AppPermissions.WorkTime.Read },

                        loadChildren: () => import('app/modules/admin/pages/work-time/work-time.routes'),
                    },

                    // {path: 'balance', loadChildren: () => import('app/modules/admin/pages/time-balance/time-balance.routes')},
                ],
            },

            {
                path: 'time-account-booking',
                data: { permission: AppPermissions.TimeAccountPosting.Create },
                loadChildren: () => import('app/modules/admin/pages/time-account-booking/time-account-booking.routes'),
            },
            {
                path: 'time-balance',
                data: { permission: AppPermissions.Report.DailyOvertimeRead },
                loadChildren: () => import('app/modules/admin/pages/time-balance/time-balance.routes'),
            },
            {
                path: 'time-account',
                data: { permission: AppPermissions.Report.TimeAccountListRead },
                loadChildren: () => import('app/modules/admin/pages/time-account-report/time-account-report.routes'),
            },
            {
                path: 'vacation',
                children: [
                    {
                        path: 'account',
                        data: { permission: AppPermissions.Vacation.Balance.Read },
                        loadChildren: () => import('app/modules/admin/pages/vacation-account/vacation-account.routes'),
                    },
                    {
                        path: 'account-overview',
                        data: { permission: AppPermissions.Vacation.Balance.Read },
                        loadChildren: () =>
                            import(
                                'app/modules/admin/pages/vacation-balance-overview/vacation-balance-overview.routes'
                            ),
                    },
                ],
            },
        ],
    },
]);
